import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleDot } from "@fortawesome/free-solid-svg-icons/faCircleDot"
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons/faCircleCheck"
import { faCircle } from "@fortawesome/free-regular-svg-icons/faCircle"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
    Box,
    componentStyles,
    HorizontalStack,
    Text,
    useColors,
    useTheme,
} from "@mallardbay/lib-react-components"

import { ASSISTANT_PLAN_STEP_STATUSES } from "~components/outfitter-assistant/assistant-tool-constants"
import type {
    AssistantPlanStep,
    AssistantPlanStepStatus,
} from "~components/outfitter-assistant/assistant-plan-tool.helpers"
import COPY from "~config/copy-constants"

interface Props {
    readonly step: AssistantPlanStep
}

export default function AssistantPlanStepRow({ step }: Props) {
    const styles = useStyles()
    const colors = useColors()
    const visual = getStepVisual(step.status, colors)
    const isActive = step.status === ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS

    return (
        <HorizontalStack style={styles.step}>
            <Box style={styles.stepIcon}>
                <FontAwesomeIcon
                    icon={visual.icon}
                    color={visual.color}
                    title={getStepStatusLabel(step.status)}
                />
            </Box>
            <Text style={getStepLabelStyle(styles, step.status)}>
                {step.label}
            </Text>
            {isActive ? (
                <Text style={styles.currentBadge}>
                    {COPY.AI_CHAT_TOOL_PLAN_CURRENT_STEP}
                </Text>
            ) : null}
        </HorizontalStack>
    )
}

/** Accessible status text so screen readers announce state, not just the label. */
function getStepStatusLabel(status: AssistantPlanStepStatus): string {
    switch (status) {
        case ASSISTANT_PLAN_STEP_STATUSES.COMPLETED:
            return COPY.AI_CHAT_TOOL_PLAN_STATUS_COMPLETED
        case ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS:
            return COPY.AI_CHAT_TOOL_PLAN_STATUS_IN_PROGRESS
        default:
            return COPY.AI_CHAT_TOOL_PLAN_STATUS_PENDING
    }
}

function getStepLabelStyle(
    styles: ReturnType<typeof useStyles>,
    status: AssistantPlanStepStatus
) {
    if (status === ASSISTANT_PLAN_STEP_STATUSES.COMPLETED) {
        return styles.stepLabelCompleted
    }
    if (status === ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS) {
        return styles.stepLabelActive
    }
    return styles.stepLabel
}

function getStepVisual(
    status: AssistantPlanStepStatus,
    colors: ReturnType<typeof useColors>
): {
    readonly icon: IconDefinition
    readonly color: string
} {
    switch (status) {
        case ASSISTANT_PLAN_STEP_STATUSES.COMPLETED:
            return { icon: faCircleCheck, color: colors.success }
        case ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS:
            return { icon: faCircleDot, color: colors.brandDefault }
        default:
            return { icon: faCircle, color: colors.secondaryText }
    }
}

function useStyles() {
    const colors = useColors()
    const theme = useTheme()

    return componentStyles({
        step: {
            alignItems: "center",
            gap: 2,
        },
        stepIcon: {
            width: "16px",
            alignItems: "center",
        },
        stepLabel: {
            color: colors.text,
            fontSize: theme.fontSizes.sm,
        },
        stepLabelActive: {
            color: colors.text,
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.fontWeights.semibold,
        },
        stepLabelCompleted: {
            color: colors.secondaryText,
            fontSize: theme.fontSizes.sm,
            textDecoration: "line-through",
        },
        currentBadge: {
            color: colors.brandDefault,
            fontSize: theme.fontSizes.xs,
            fontWeight: theme.fontWeights.semibold,
        },
    })
}
